import React from 'react';
import { AlertTriangle, AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import i18next from 'i18next';
import { reportError } from '../../utils/errorReporting';

/**
 * ErrorBoundary — the ONE error boundary implementation in the app.
 *
 * DESIGN_DNA.md §3.12 / §2.8: three variants —
 *   - `variant="page"`     top-level page shell
 *   - `variant="subview"`  tab / conditionally-rendered subview content
 *   - `variant="widget"`   a single widget inside a dashboard
 *   - (no variant)         the legacy generic fallback, unchanged in behaviour
 *
 * WHY THIS FILE CHANGED
 * ---------------------
 * ~90 files import this boundary and ~80 call sites across consent, dataflow,
 * settings, admin, dpo, assessment-center, organization and privilege-review
 * pass `variant` / `viewName` / `t` to it. Until now this component accepted
 * only `fallback` / `onReset` / `children`, so EVERY one of those props was
 * silently dropped on the floor. Two consequences, both live in production:
 *
 *   1. A crashed subview stayed crashed. `hasError` never cleared when the
 *      user switched tabs, so the only recovery was a full page reload — the
 *      auto-reset-on-viewName-change that §3.12 relies on did not exist here.
 *   2. Compliance with §2.8/§3.12 was nominal. The code passed a grep for
 *      `variant="page"` while none of the variant behaviour was implemented.
 *
 * A second, divergent copy of this component lived at
 * `modules/classification/components/shared/ErrorBoundary.js` and DID
 * implement the variants. That file is now a re-export of this one, so there
 * is exactly ONE implementation rather than two.
 *
 * BACK-COMPATIBILITY CONTRACT
 * ---------------------------
 * Call sites that pass NO `variant` (ConsentList.js and ~50 others) keep the
 * generic fallback and the existing `fallback` / `onReset` props verbatim.
 * Only the token palette of that fallback changed — see below.
 *
 * i18n: strings resolve through i18next against the `common` namespace with a
 * literal `defaultValue`, NOT through the caller's `t` prop. The `t` prop is
 * still accepted (so no call site needed editing) but deliberately ignored:
 * callers pass a MODULE-scoped `t` — `useTranslation('consent')` — and the
 * classification copy's keys (`subview.error.message`, `classification.error.title`)
 * exist only in the `classification` namespace. Honouring `t` would have
 * rendered the raw key string "subview.error.message" as visible UI text on
 * every consent, dpo and admin crash. `defaultValue` makes a missing key
 * structurally unable to reach the screen.
 *
 * Error reporting: routed through `reportError()` — the real, vendor-pluggable
 * sink (console + `window.__PRIVACYGUARD_ERROR_SINK__`). The classification
 * copy instead POSTed to `/api/v1/audit/client-error`; that route does not
 * exist anywhere in the backend, so those reports went to a 404 and were
 * swallowed by `.catch(() => {})`. The POST is intentionally NOT carried over.
 *
 * DNA tokens: the fallbacks use §4.1 slate ink and the §3.1 card shell
 * (`rounded-xl shadow-sm border border-slate-200`). The previous generic
 * fallback used `text-gray-900` / `text-gray-600` (§4.1 forbids gray-* for
 * text-on-white) and `rounded-lg shadow-md`; the classification copy used
 * `text-gray-900` / `text-gray-500` / `border-gray-300` (§9 forbids the
 * gray-* border family). Neither survived the merge.
 *
 * `viewName` vs `viewLabel`: `viewName` is an internal identifier (a raw React
 * component name such as "RequirementRegisterView") used ONLY as the
 * auto-reset key in `componentDidUpdate` and as error-report metadata — it is
 * never itself rendered. `viewLabel` is the OPTIONAL human-facing caption a
 * call site may pass, already translated by the caller's own module `t()`
 * (this class component has no module-scoped `t` of its own — see the i18n
 * note above). The subview fallback shows `viewLabel` only, and shows nothing
 * at all when a call site has not supplied one: a missing caption is silence,
 * never a fallback to the raw identifier, so an un-migrated call site cannot
 * leak an untranslated CamelCase name onto the screen.
 */

const T = (key, defaultValue) =>
  i18next.t(key, { ns: 'common', defaultValue });

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, resetKey: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const { variant, viewName, name } = this.props;

    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, errorInfo?.componentStack);
    }

    // Keys are added only when the call site actually supplied them, so a
    // boundary with no `variant` reports the exact `{ boundary }` meta shape
    // it always has. Enriching the payload must not change the contract for
    // the ~50 legacy call sites that pass no variant.
    const meta = { boundary: 'ErrorBoundary' };
    if (variant) meta.variant = variant;
    // The crashing surface's identity, whichever prop the call site used.
    const view = viewName || name;
    if (view) meta.view = view;

    reportError(error, errorInfo, meta);
  }

  componentDidUpdate(prevProps) {
    // §3.12 — a subview boundary auto-clears when the active view changes, so
    // switching tabs recovers a crashed tab. Without this the boundary latches
    // and the user must reload the whole page.
    if (
      this.props.variant === 'subview' &&
      prevProps.viewName !== this.props.viewName &&
      this.state.hasError
    ) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    // Bump resetKey so children REMOUNT rather than re-render with the stale
    // state that crashed them.
    this.setState((prev) => ({
      hasError: false,
      error: null,
      resetKey: prev.resetKey + 1,
    }));
    this.props.onReset?.();
  };

  handleGoBack = () => {
    this.setState((prev) => ({
      hasError: false,
      error: null,
      resetKey: prev.resetKey + 1,
    }));
    this.props.onGoBack?.();
  };

  renderPageFallback() {
    const { onGoBack } = this.props;
    // §3.1 card shell (`bg-white rounded-xl shadow-sm border border-slate-200`)
    // — this is what every `variant="page"` site rendered before the merge
    // (the old shared boundary had no variant support, so the page shell was
    // the ONLY fallback ~15 page roots ever showed). A bare centred block with
    // no card is a visual regression, not an intentional redesign.
    return (
      <div className="flex items-center justify-center min-h-[400px] p-8">
        <div
          role="alert"
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 max-w-md text-center"
        >
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4 mx-auto">
            <AlertTriangle size={28} className="text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            {T('somethingWentWrong', 'Something went wrong')}
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            {T('sectionError', 'An error occurred while loading this section.')}
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleRetry}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            >
              <RefreshCw size={14} />
              {T('tryAgain', 'Try Again')}
            </button>
            {/* Rendered only when the page actually supplied a handler — a
                "Back" button wired to nothing is a dead control, and most page
                call sites pass no `onGoBack`. */}
            {onGoBack && (
              <button
                type="button"
                onClick={this.handleGoBack}
                className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                <ArrowLeft size={14} />
                {T('back', 'Back')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderSubviewFallback() {
    const { viewLabel } = this.props;
    return (
      <div
        role="alert"
        className="flex flex-col items-center justify-center p-10 text-center bg-white rounded-xl shadow-sm border border-red-200"
      >
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-3">
          <AlertTriangle size={22} className="text-red-500" />
        </div>
        <p className="text-sm font-medium text-slate-800 mb-1">
          {T('sectionError', 'An error occurred while loading this section.')}
        </p>
        {/* `viewLabel` only — never `viewName`. `viewName` is a raw internal
            identifier (see the file header note) and must never reach the
            screen; a call site that has not supplied a translated `viewLabel`
            gets silence here, not the CamelCase fallback it used to show. */}
        {viewLabel && <p className="text-xs text-slate-400 mb-4">{viewLabel}</p>}
        <button
          type="button"
          onClick={this.handleRetry}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
        >
          <RefreshCw size={14} />
          {T('tryAgain', 'Try Again')}
        </button>
      </div>
    );
  }

  renderWidgetFallback() {
    return (
      <div
        role="alert"
        className="bento-card flex flex-col items-center justify-center p-6 text-center bg-red-50/50"
      >
        <AlertCircle size={24} className="text-red-400 mb-2" />
        <p className="text-xs font-medium text-red-600">
          {T('widgetUnavailable', 'This widget is unavailable')}
        </p>
      </div>
    );
  }

  renderGenericFallback() {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-8">
        <div
          role="alert"
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 max-w-md text-center"
        >
          <AlertTriangle size={48} className="text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            {T('somethingWentWrong', 'Something went wrong')}
          </h2>
          <p className="text-slate-600 mb-6">
            {T('sectionError', 'An error occurred while loading this section.')}
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            {T('tryAgain', 'Try Again')}
          </button>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      // An explicit `fallback` element always wins, for every variant.
      if (this.props.fallback) {
        return this.props.fallback;
      }

      switch (this.props.variant) {
        case 'page':
          return this.renderPageFallback();
        case 'subview':
          return this.renderSubviewFallback();
        // §3.12 prose names this variant "component"; every call site in the
        // codebase spells it "widget". Both are accepted so neither spelling
        // can silently fall through to the generic fallback.
        case 'widget':
        case 'component':
          return this.renderWidgetFallback();
        default:
          return this.renderGenericFallback();
      }
    }

    return (
      <React.Fragment key={this.state.resetKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

export default ErrorBoundary;
